/**
 * Информационные экраны Mini App: FAQ (ШАГ 20), Коммьюнити (ШАГ 19),
 * «Как это работает» (ШАГ 22) и выбор языка (ШАГ 17).
 *
 * Открываются шторкой снизу — приём из design-spec §5.5.
 * Тексты приходят с сервера (`/api/texts`), поэтому словарь один на бота и приложение.
 */
import { api } from '../lib/api.js';
import type { AppConfig } from '../lib/api.js';
import { haptic, openLink } from '../lib/telegram.js';
import { useApp } from '../state/app.js';

export type SheetId = 'faq' | 'community' | 'how' | 'language' | null;

/** Языки экрана ШАГ 17 — подписи на своём языке. */
const LANGUAGES: Array<{ code: string; label: string }> = [
  { code: 'ar', label: 'العربية' },
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'id', label: 'Bahasa Indonesia' },
  { code: 'it', label: 'Italiano' },
  { code: 'fil', label: 'Filipino' },
  { code: 'pt', label: 'Português' },
  { code: 'ru', label: 'Русский' },
];

/** Пункты FAQ: те же ключи, что в боте. */
const FAQ_ITEMS = [1, 2, 3, 4, 5];

const contactUrl = (contact: string): string => `https://t.me/${contact.replace(/^@/, '')}`;

export function InfoSheet({
  id,
  config,
  onClose,
}: {
  id: SheetId;
  config: AppConfig | null;
  onClose: () => void;
}) {
  const { me, t, refresh, showToast, reloadTexts } = useApp();

  if (id === null) return null;

  async function switchLanguage(code: string): Promise<void> {
    haptic();
    try {
      await api.updateSettings({ language: code });
      await Promise.all([reloadTexts(code), refresh()]);
      showToast(t('toast.language'));
      onClose();
    } catch {
      showToast(t('common.error'));
    }
  }

  return (
    <div
      className="sheet-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="sheet">
        <div className="sheet-handle" />

        {id === 'faq' && (
          <>
            <p className="sheet-title">{t('screen.faq.intro')}</p>
            {/* пункты раскрываются по одному, как в боте: простыню из пяти вопросов не читают */}
            {FAQ_ITEMS.map((index) => (
              <details key={index} className="faq-item">
                <summary className="faq-question">{t(`faq.${index}.title`)}</summary>
                <p className="faq-answer">{t(`faq.${index}.body`)}</p>
              </details>
            ))}
          </>
        )}

        {id === 'how' && <p className="sheet-text">{t('screen.how.body')}</p>}

        {id === 'community' && (
          <>
            <p className="sheet-text">{t('screen.community.body')}</p>
            <div className="stack" style={{ marginTop: 14 }}>
              <button
                type="button"
                className="cta cta-block"
                onClick={() => openLink(contactUrl(config?.brand.communityChannel ?? ''))}
              >
                {t('button.community.main')}
              </button>
              <button
                type="button"
                className="cta cta-secondary cta-block"
                onClick={() => openLink(contactUrl(config?.brand.supportContact ?? ''))}
              >
                {t('button.community.support')}
              </button>
            </div>
          </>
        )}

        {id === 'language' && (
          <>
            <h2 className="card-title">{t('screen.language.body')}</h2>
            <div className="lang-grid">
              {LANGUAGES.map((language) => (
                <button
                  key={language.code}
                  type="button"
                  className={`chip ${me?.user.language === language.code ? 'is-active' : ''}`}
                  onClick={() => void switchLanguage(language.code)}
                >
                  {language.label}
                </button>
              ))}
            </div>
          </>
        )}

        <button type="button" className="cta cta-secondary cta-block" style={{ marginTop: 16 }} onClick={onClose}>
          {t('common.back')}
        </button>
      </div>
    </div>
  );
}

/** Список «Ещё» на экране аккаунта: точки входа в информационные экраны. */
export function InfoLinks({ onOpen }: { onOpen: (id: SheetId) => void }) {
  const { t } = useApp();

  const rows: Array<{ id: Exclude<SheetId, null>; icon: string; labelKey: string }> = [
    { id: 'how', icon: '❓', labelKey: 'button.menu.how' },
    { id: 'faq', icon: '📖', labelKey: 'button.menu.faq' },
    { id: 'community', icon: '👥', labelKey: 'button.menu.community' },
    { id: 'language', icon: '🌐', labelKey: 'button.settings.language' },
  ];

  return (
    <section className="card">
      <span className="kicker">{t('screen.menu.title')}</span>
      <div className="info-list">
        {rows.map((row) => (
          <button
            key={row.id}
            type="button"
            className="info-row"
            onClick={() => {
              haptic();
              onOpen(row.id);
            }}
          >
            <span className="info-icon">{row.icon}</span>
            <span className="grow">{t(row.labelKey)}</span>
            <span className="info-chevron">›</span>
          </button>
        ))}
      </div>
    </section>
  );
}
